/**
 * The pure half of the mail manager (contract v10.40): the words, the client token and
 * how a failure reads. Out of the components so the tests pin them without rendering.
 */
import { mailFailureReason } from '@/shared/emails/mailFailure';
import { notAnAddress, recipientsText } from '@/shared/emails/sending';

import type { Outgoing, OutgoingKind } from './types';

/** As many as the back takes in each field (`MAX_RECIPIENTS`). */
export const MAX_RECIPIENTS = 30;

/** Files one message carries, the original's and the person's own (`MAX_ATTACHMENTS`). */
export const MAX_ATTACHMENTS = 30;

export const COMPOSE_TITLES: Record<OutgoingKind, string> = {
  new: 'Correo nuevo',
  reply: 'Responder',
  reply_all: 'Responder a todos',
  forward: 'Reenviar',
};

/** How a line of «Respuestas y reenvíos» starts. */
export const SENT_KIND: Record<OutgoingKind, string> = {
  new: 'Correo',
  reply: 'Respuesta',
  reply_all: 'Respuesta a todos',
  forward: 'Reenvío',
};

/**
 * The back's own reasons are already a sentence for the shop: for a message, they end in
 * «no ha salido nada.»; for its copy in Sent, in «Reintentar.», «queda pendiente.» or
 * «guardar la copia.». They go on screen as they are. Anything else is what a mail server
 * said, and goes through `mailFailureReason`.
 */
const OWN_SENTENCE = /(no ha salido nada|Reintentar|queda pendiente|guardar la copia)\.$/;

/** Why it was not sent, as the end of «No se envió a …». */
export function outgoingFailureText(raw: string): string {
  return OWN_SENTENCE.test(raw) ? `. ${raw}` : `: ${mailFailureReason(raw)}.`;
}

/** What the back said about the copy in Sent, when it is a sentence for the shop. */
export function ownCopySentence(raw: string | null): string | null {
  return raw && OWN_SENTENCE.test(raw) ? raw : null;
}

/**
 * The token that makes «Enviar» idempotent (contract v10.40): chosen when the form opens,
 * so a double click or a retry after a lost answer is the same message, never two.
 */
export function newClientToken(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * The groups a message was written to (contract v10.44), as the end of «Correo a …»: «al
 * grupo «Tiendas»», «a los grupos «Tiendas» y «Comerciales»». `capital` starts a sentence.
 */
export function groupsText(names: readonly string[], { capital = false } = {}): string {
  const quoted = names.map((name) => `«${name}»`);
  const list =
    quoted.length > 1 ? `${quoted.slice(0, -1).join(', ')} y ${quoted.at(-1)}` : (quoted[0] ?? '');
  const text = quoted.length > 1 ? `a los grupos ${list}` : `al grupo ${list}`;
  return capital ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
}

/**
 * Who a message was written to, as the end of «Enviando …», «No se envió …» or «Respuesta
 * …»: «a ana@x.es», «a ana@x.es y al grupo «Tiendas»». When «Para» was left empty for the
 * groups, the back put the mailbox's own address there (contract v10.44), and the line
 * says «al grupo «Tiendas»» alone: «a compras@… y al grupo» reads as if it had also gone
 * to the shop. `groups: false` leaves the groups out, for a line that names them below.
 */
export function sentToText(
  outgoing: Pick<Outgoing, 'to_addresses' | 'group_names' | 'from_address' | 'mailbox'>,
  { groups: withGroups = true } = {},
): string {
  const groups = outgoing.group_names ?? [];
  const to = outgoing.to_addresses;
  const own = [outgoing.from_address, outgoing.mailbox].map((each) => each?.toLowerCase());
  const onlyGroups = groups.length > 0 && to.length === 1 && own.includes(to[0]?.toLowerCase());
  if (onlyGroups) return groupsText(groups);
  const people = `a ${recipientsText(to)}`;
  return withGroups && groups.length > 0 ? `${people} y ${groupsText(groups)}` : people;
}

/** The error of an address field, or undefined when it is fine. */
export function addressError(addresses: string[], required: boolean): string | undefined {
  if (required && addresses.length === 0) return 'Pon al menos una dirección.';
  if (addresses.length > MAX_RECIPIENTS) return `Como mucho ${MAX_RECIPIENTS} direcciones.`;
  const bad = notAnAddress(addresses);
  return bad ? `«${bad}» no es una dirección de correo.` : undefined;
}

/**
 * A file the person attached (contract v10.41), as the form follows it: uploaded as soon
 * as it is chosen, so «Enviar» only carries its id.
 */
export type OwnFile =
  | { key: string; name: string; size: number; state: 'uploading' }
  | { key: string; name: string; size: number; state: 'done'; id: string }
  | { key: string; name: string; size: number; state: 'failed'; error: string };

/** «15,0 MB», written as the back writes it in its own refusals. */
export function megabytes(size: number): string {
  return `${(size / 1_048_576).toFixed(1).replace('.', ',')} MB`;
}

/** Why a chosen file is not even uploaded, or undefined to upload it. */
export function fileProblem(size: number, limit: number): string | undefined {
  if (size === 0) return 'Está vacío: no se ha adjuntado.';
  if (size > limit)
    return `Pasa de ${megabytes(limit)}, lo máximo de un correo: no se ha adjuntado.`;
  return undefined;
}

/**
 * What stops «Enviar» on the attachments, or undefined: the same limits the back checks
 * (30 files, `max_attachment_bytes` in all), and a file that could not be attached, which
 * would otherwise be left out without anybody noticing.
 */
export function attachmentsProblem(
  original: { count: number; bytes: number },
  own: OwnFile[],
  limit: number,
): string | undefined {
  if (own.some((file) => file.state === 'failed')) {
    return 'Quita los ficheros que no se han podido adjuntar.';
  }
  const kept = own.filter((file) => file.state !== 'failed');
  if (original.count + kept.length > MAX_ATTACHMENTS) {
    return `Como mucho ${MAX_ATTACHMENTS} adjuntos por correo.`;
  }
  const total = original.bytes + kept.reduce((sum, file) => sum + file.size, 0);
  if (limit > 0 && total > limit) {
    return `Los adjuntos ocupan ${megabytes(total)} y el máximo es ${megabytes(limit)}: quita alguno.`;
  }
  return undefined;
}
